import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Save, Loader2, Eye, RotateCcw, Palette, Code, ChevronDown, ChevronRight, Image, Send, AlertTriangle, Database, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEmailTemplates = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [baseSettings, setBaseSettings] = useState({ logo_url: '', logo_text: 'SENDBOX', footer_text: '', primary_color: '#3b82f6', accent_color: '#06b6d4' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editData, setEditData] = useState({});
  const [testEmail, setTestEmail] = useState('');
  const [showTestInput, setShowTestInput] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [showDbStatus, setShowDbStatus] = useState(false);
  const [resetAllConfirm, setResetAllConfirm] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { if (token) loadData(); }, [token]);

  const loadData = async () => {
    try {
      const [tplRes, baseRes] = await Promise.all([
        axios.get(`${API}/admin/email-templates`, { headers }),
        axios.get(`${API}/admin/email-templates/base`, { headers })
      ]);
      setTemplates(tplRes.data);
      setBaseSettings(baseRes.data);
      const edits = {};
      tplRes.data.forEach(t => { edits[t.template_id] = { subject: t.subject, body: t.body }; });
      setEditData(edits);
    } catch (e) {
      toast({ title: 'Erreur de chargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (tid) => {
    setSaving(tid);
    try {
      await axios.put(`${API}/admin/email-templates/${tid}`, editData[tid], { headers });
      toast({ title: 'Template sauvegardé' });
      loadData();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const resetTemplate = async (tid) => {
    setSaving(tid + '_reset');
    try {
      await axios.post(`${API}/admin/email-templates/reset/${tid}`, {}, { headers });
      toast({ title: 'Template réinitialisé' });
      loadData();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const previewTemplate = async (tid) => {
    setSaving(tid + '_preview');
    try {
      // Save first to preview latest changes
      await axios.put(`${API}/admin/email-templates/${tid}`, editData[tid], { headers });
      const res = await axios.post(`${API}/admin/email-templates/preview/${tid}`, {}, { headers });
      setPreviewSubject(res.data.subject);
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (e) {
      toast({ title: 'Erreur de prévisualisation', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const saveBase = async () => {
    setSaving('_base');
    try {
      await axios.put(`${API}/admin/email-templates/base`, baseSettings, { headers });
      toast({ title: 'Paramètres de base sauvegardés' });
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const sendTestEmail = async (tid) => {
    if (!testEmail.trim()) {
      toast({ title: 'Entrez une adresse email', variant: 'destructive' });
      return;
    }
    setSaving(tid + '_test');
    try {
      // Save template first
      await axios.put(`${API}/admin/email-templates/${tid}`, editData[tid], { headers });
      const res = await axios.post(`${API}/admin/email-templates/test/${tid}`, { email: testEmail }, { headers });
      toast({ title: 'Email de test envoyé', description: res.data.message });
      setShowTestInput(null);
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur d\'envoi', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const loadDbStatus = async () => {
    setSaving('db_status');
    try {
      const res = await axios.get(`${API}/admin/email-templates/db-status`, { headers });
      setDbStatus(res.data);
      setShowDbStatus(true);
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const resetAllTemplates = async () => {
    setSaving('reset_all');
    try {
      const res = await axios.post(`${API}/admin/email-templates/reset-all`, {}, { headers });
      toast({ title: 'Templates réinitialisés', description: res.data.message });
      setResetAllConfirm(false);
      loadData();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const forceDefaults = async () => {
    setSaving('force_defaults');
    try {
      const res = await axios.post(`${API}/admin/email-templates/force-defaults`, {}, { headers });
      toast({ title: 'Réinitialisation complète', description: res.data.message });
      setResetAllConfirm(false);
      loadData();
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <Toaster />
      <NavigationMenu />

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-slate-900 border border-blue-500/20 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="email-preview-modal">
            <div className="flex items-center justify-between p-4 border-b border-blue-500/10">
              <div>
                <p className="text-xs text-blue-300/50">Sujet :</p>
                <p className="text-sm text-white font-medium">{previewSubject}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)} className="text-blue-300/60">Fermer</Button>
            </div>
            <div className="overflow-auto max-h-[70vh]">
              <iframe srcDoc={previewHtml} className="w-full h-[600px] border-0" title="Prévisualisation" />
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-400" />
                <h1 className="text-xl font-semibold text-white">Templates email</h1>
              </div>
            </div>
          </div>

        <div className="space-y-6">
        {/* Maintenance Section */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-6" data-testid="email-maintenance-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/20 rounded-lg"><AlertTriangle className="w-5 h-5 text-amber-400" /></div>
            <div>
              <h2 className="text-lg font-semibold text-white">Maintenance & Diagnostic</h2>
              <p className="text-xs text-amber-300/70">Résoudre les problèmes de synchronisation entre le code et la base de données</p>
            </div>
          </div>
          
          <div className="bg-slate-900/50 rounded-xl p-4 mb-4 text-sm text-amber-200/80">
            <p className="font-medium mb-2">⚠️ Problème de production courant :</p>
            <p className="text-xs text-amber-200/60">Si les emails en production n'utilisent pas les derniers templates, c'est probablement dû à des templates personnalisés obsolètes dans la base de données qui écrasent les valeurs par défaut du code.</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={loadDbStatus} disabled={!!saving} variant="outline" className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="btn-db-status">
              {saving === 'db_status' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Voir état de la DB
            </Button>
            <Button onClick={() => setResetAllConfirm(true)} disabled={!!saving} variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="btn-reset-all">
              <RefreshCw className="w-4 h-4 mr-2" /> Réinitialiser tous les templates
            </Button>
          </div>

          {/* DB Status Modal */}
          {showDbStatus && dbStatus && (
            <div className="mt-4 bg-slate-900/70 rounded-xl p-4 border border-amber-500/10" data-testid="db-status-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white">État des templates dans la DB</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowDbStatus(false)} className="text-amber-300/50 text-xs">Fermer</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{dbStatus.total_in_db}</p>
                  <p className="text-xs text-amber-300/60">Entrées en DB</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-400">{dbStatus.customized_templates?.length || 0}</p>
                  <p className="text-xs text-amber-300/60">Templates personnalisés</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{dbStatus.base_settings_customized ? 'Oui' : 'Non'}</p>
                  <p className="text-xs text-amber-300/60">Base personnalisée</p>
                </div>
              </div>
              {dbStatus.customized_templates?.length > 0 && (
                <div className="text-xs text-amber-200/70">
                  <p className="font-medium mb-1">Templates personnalisés :</p>
                  <div className="flex flex-wrap gap-1">
                    {dbStatus.customized_templates.map(t => (
                      <span key={t} className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reset All Confirmation */}
          {resetAllConfirm && (
            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="reset-confirm-panel">
              <p className="text-sm text-red-200 mb-3">
                <strong>Attention :</strong> Cette action supprimera toutes les personnalisations de templates email. Les templates utiliseront les valeurs par défaut du code.
              </p>
              <div className="flex gap-2">
                <Button onClick={resetAllTemplates} disabled={!!saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirm-reset">
                  {saving === 'reset_all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Confirmer la réinitialisation
                </Button>
                <Button onClick={forceDefaults} disabled={!!saving} variant="outline" className="border-red-500/30 text-red-300" data-testid="btn-force-defaults">
                  {saving === 'force_defaults' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Réinitialisation complète (+ base)
                </Button>
                <Button onClick={() => setResetAllConfirm(false)} variant="ghost" className="text-slate-400">Annuler</Button>
              </div>
            </div>
          )}
        </div>

        {/* Base Settings */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6" data-testid="email-base-settings">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-blue-500/20 rounded-lg"><Palette className="w-5 h-5 text-blue-400" /></div>
            <div>
              <h2 className="text-lg font-semibold text-white">Apparence globale</h2>
              <p className="text-xs text-blue-300/50">Logo, couleurs et pied de page communs à tous les emails</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-blue-300 mb-1">URL du logo</label>
              <div className="flex gap-2">
                <input value={baseSettings.logo_url} onChange={e => setBaseSettings(p => ({ ...p, logo_url: e.target.value }))}
                  placeholder="https://..." className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  data-testid="base-logo-url" />
                <div className="flex-shrink-0 w-10 h-10 bg-slate-900/50 border border-blue-500/20 rounded-xl flex items-center justify-center">
                  {baseSettings.logo_url ? <img src={baseSettings.logo_url} alt="" className="max-h-8 max-w-8 object-contain" /> : <Image className="w-4 h-4 text-blue-300/30" />}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-1">Texte du logo</label>
              <input value={baseSettings.logo_text} onChange={e => setBaseSettings(p => ({ ...p, logo_text: e.target.value }))}
                placeholder="SENDBOX" className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                data-testid="base-logo-text" />
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-1">Couleur principale</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={baseSettings.primary_color} onChange={e => setBaseSettings(p => ({ ...p, primary_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-blue-500/20 cursor-pointer bg-transparent" data-testid="base-primary-color" />
                <input value={baseSettings.primary_color} onChange={e => setBaseSettings(p => ({ ...p, primary_color: e.target.value }))}
                  className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-blue-300 mb-1">Couleur d'accent</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={baseSettings.accent_color} onChange={e => setBaseSettings(p => ({ ...p, accent_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-blue-500/20 cursor-pointer bg-transparent" data-testid="base-accent-color" />
                <input value={baseSettings.accent_color} onChange={e => setBaseSettings(p => ({ ...p, accent_color: e.target.value }))}
                  className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/40" />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-blue-300 mb-1">Pied de page personnalisé <span className="text-blue-300/30">(HTML)</span></label>
              <textarea value={baseSettings.footer_text} onChange={e => setBaseSettings(p => ({ ...p, footer_text: e.target.value }))}
                placeholder="Laissez vide pour le pied de page par défaut" rows={2}
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-blue-300/30 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none font-mono"
                data-testid="base-footer-text" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={saveBase} disabled={saving === '_base'} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-base-btn">
              {saving === '_base' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Sauvegarder
            </Button>
          </div>
        </div>

        {/* Templates */}
        <div className="space-y-3" data-testid="email-templates-list">
          {templates.map(tpl => {
            const isExpanded = expandedTemplate === tpl.template_id;
            const edit = editData[tpl.template_id] || {};
            return (
              <div key={tpl.template_id} className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
                {/* Header */}
                <button onClick={() => setExpandedTemplate(isExpanded ? null : tpl.template_id)}
                  className="w-full flex items-center gap-3 p-5 text-left hover:bg-blue-500/5 transition-colors" data-testid={`tpl-${tpl.template_id}-toggle`}>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-blue-400" /> : <ChevronRight className="w-4 h-4 text-blue-400" />}
                  <Mail className="w-4 h-4 text-blue-400" />
                  <div className="flex-1">
                    <h3 className="text-white font-medium text-sm">{tpl.name}</h3>
                    <p className="text-xs text-blue-300/50">{tpl.description}</p>
                  </div>
                  {tpl.is_customized && <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">Personnalisé</span>}
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-blue-500/10 p-5 space-y-4">
                    {/* Variables */}
                    <div>
                      <p className="text-xs text-blue-300/50 mb-2">Variables disponibles :</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tpl.variables.map(v => (
                          <button key={v} onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}
                            className="text-xs bg-slate-900/60 border border-blue-500/15 text-blue-300/70 px-2 py-1 rounded-lg hover:bg-blue-500/10 hover:text-blue-300 transition-colors font-mono cursor-copy"
                            title={`Copier {{${v}}}`}>
                            {'{{' + v + '}}'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm text-blue-300 mb-1">Sujet</label>
                      <input value={edit.subject || ''} onChange={e => setEditData(p => ({ ...p, [tpl.template_id]: { ...p[tpl.template_id], subject: e.target.value } }))}
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 font-mono"
                        data-testid={`tpl-${tpl.template_id}-subject`} />
                    </div>

                    {/* Body */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm text-blue-300 flex items-center gap-1.5"><Code className="w-3.5 h-3.5" /> Corps HTML</label>
                        <span className="text-xs text-blue-300/30">Classes CSS : title, subtitle, btn, info-box, warning, message-box...</span>
                      </div>
                      <textarea value={edit.body || ''} onChange={e => setEditData(p => ({ ...p, [tpl.template_id]: { ...p[tpl.template_id], body: e.target.value } }))}
                        rows={10}
                        className="w-full bg-slate-900/50 border border-blue-500/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-y font-mono leading-relaxed"
                        data-testid={`tpl-${tpl.template_id}-body`} />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-2 flex-wrap">
                      <Button onClick={() => saveTemplate(tpl.template_id)} disabled={!!saving}
                        className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`tpl-${tpl.template_id}-save`}>
                        {saving === tpl.template_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Sauvegarder
                      </Button>
                      <Button onClick={() => previewTemplate(tpl.template_id)} disabled={!!saving} variant="outline"
                        className="border-blue-500/30 text-blue-300" data-testid={`tpl-${tpl.template_id}-preview`}>
                        {saving === tpl.template_id + '_preview' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Prévisualiser
                      </Button>
                      <Button onClick={() => setShowTestInput(showTestInput === tpl.template_id ? null : tpl.template_id)} disabled={!!saving} variant="outline"
                        className="border-green-500/30 text-green-300" data-testid={`tpl-${tpl.template_id}-test-toggle`}>
                        <Send className="w-4 h-4 mr-2" /> Envoyer un test
                      </Button>
                      {tpl.is_customized && (
                        <Button onClick={() => resetTemplate(tpl.template_id)} disabled={!!saving} variant="ghost"
                          className="text-blue-300/50 hover:text-blue-300" data-testid={`tpl-${tpl.template_id}-reset`}>
                          <RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser
                        </Button>
                      )}
                    </div>

                    {/* Test Email Input */}
                    {showTestInput === tpl.template_id && (
                      <div className="flex items-center gap-2 mt-3 p-3 bg-green-500/5 border border-green-500/20 rounded-xl" data-testid={`tpl-${tpl.template_id}-test-input`}>
                        <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                          placeholder="votre@email.com" type="email"
                          className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500/40"
                          onKeyDown={e => { if (e.key === 'Enter') sendTestEmail(tpl.template_id); }}
                          data-testid={`tpl-${tpl.template_id}-test-email`} />
                        <Button onClick={() => sendTestEmail(tpl.template_id)} disabled={!!saving} size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white" data-testid={`tpl-${tpl.template_id}-send-test`}>
                          {saving === tpl.template_id + '_test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
        </div>
      </main>
      
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default AdminEmailTemplates;
